import apiClient from './client'
import type { AxiosRequestConfig } from 'axios'

type QuietRequestConfig = AxiosRequestConfig & { skipGlobalErrorToast?: boolean }

export const AI_EDITORIAL_DECLARATION_VERSION = 'editorial-review-v1'

export type AiContentClassification = 'ai_generated' | 'ai_modified' | 'mixed'

export interface AiComplianceStatus {
  accepted: boolean
  policy: {
    version: string
    sha256: string
    rules: string[]
  }
  revisions: Array<{
    publicId: string
    resourceType: string
    resourceId: string
    fieldGroup: string
    locale: string | null
    classification: AiContentClassification | 'human'
    contentSha256: string
    feature: string | null
    appliedAt: string
    reviewed: boolean
    reviewedAt: string | null
  }>
}

export const aiContentComplianceApi = {
  async getStatus(query?: { resourceType?: string, resourceId?: string | number, locale?: string }): Promise<AiComplianceStatus> {
    const config: QuietRequestConfig = {
      params: query,
      skipGlobalErrorToast: true,
    }
    const { data } = await apiClient.get<AiComplianceStatus>('/api/v1/ai-content-compliance', config)
    return data
  },

  async acceptPolicy(
    status: AiComplianceStatus,
    locale: string,
    presentation: {
      title: string
      regulatoryContext: string
      intro: string
      rules: string[]
      scopeNote: string
      confirmation: string
      eyebrow: string
      versionLabel: string
      scrollEndConfirmed: true
      links: Array<{
        label: string
        url: string
      }>
    },
  ): Promise<void> {
    await apiClient.post('/api/v1/ai-content-compliance', {
      action: 'accept_policy',
      accepted: true,
      policyVersion: status.policy.version,
      policySha256: status.policy.sha256,
      locale,
      presentation,
    }, { skipGlobalErrorToast: true } as QuietRequestConfig)
  },

  async applyAiContent(input: {
    resourceType: string
    resourceId: string | number
    fieldGroup: string
    locale?: string
    classification: AiContentClassification
    content: unknown
    feature: string
  }): Promise<void> {
    await apiClient.post('/api/v1/ai-content-compliance', {
      action: 'apply_ai_content',
      ...input,
      resourceId: String(input.resourceId),
    }, { skipGlobalErrorToast: true } as QuietRequestConfig)
  },

  async reviewContent(input: {
    resourceType: string
    resourceId: string | number
    fieldGroup: string
    locale?: string
    content: unknown
  }): Promise<void> {
    await apiClient.post('/api/v1/ai-content-compliance', {
      action: 'review_content',
      confirmed: true,
      declarationVersion: AI_EDITORIAL_DECLARATION_VERSION,
      ...input,
      resourceId: String(input.resourceId),
    }, { skipGlobalErrorToast: true } as QuietRequestConfig)
  },

  async declareHumanReplacement(input: {
    resourceType: string
    resourceId: string | number
    fieldGroup: string
    locale?: string
    content: unknown
    feature: string
  }): Promise<void> {
    await apiClient.post('/api/v1/ai-content-compliance', {
      action: 'declare_human_replacement',
      confirmed: true,
      ...input,
      resourceId: String(input.resourceId),
    }, { skipGlobalErrorToast: true } as QuietRequestConfig)
  },
}
